/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import Axios from "axios";
import { jwtDecode } from "jwt-decode";
import { TokenManager } from "./TokenManager";
import { getCookie } from "../utils/cookiesHelper";

export const ACCESS_TOKEN_EXP_IN = "ACCESS_TOKEN_EXP_IN";
export const thresholdTime = 4 * 60 * 1000;

export class NetworkOps {
  _httpClient;

  authToken = null;

  hashToken = null;

  contentType = null;

  isRefreshingToken = false;

  refreshTokenQueue = [];

  customHeaders = {}; // Object to store custom headers

  /**
   * Creates an instance of NetworkOps.
   * It has capabilities to add bearer token
   * @param {options} options axios options
   */
  constructor(options = {}) {
    this._httpClient = Axios.create({
      ...options
    });

    this._httpClient.interceptors.request.use(this._addHeaders);
  }

  // Method to set custom headers
  setCustomHeaders = (headers = {}) => {
    this.customHeaders = { ...this.customHeaders, ...headers };
  };

  // Clear custom headers
  clearCustomHeaders = () => {
    this.customHeaders = {};
  };

  isStr(str) {
    return typeof str === "string";
  }

  encode(str = "") {
    if (!this.isStr(str)) {
      throw new Error("Please provide string to encode.");
    }

    return encodeURIComponent(str);
  }

  decode(str = "") {
    if (!this.isStr(str)) {
      throw new Error("Please provide string to decode");
    }

    return decodeURIComponent(str.replace(/%2B/g, "%20"));
  }

  _addHeaders = async (request) => {
    request.headers = request.headers || {};
    if (request.skipAuthRetry) {
      if (this.hashToken) {
        request.headers.Authorization = `Bearer ${this.hashToken}`;
      } else if (this.authToken) {
        request.headers.Authorization = `Bearer ${this.authToken}`;
      }
      return request;
    }

    if (this.hashToken) {
      request.headers.Authorization = `Bearer ${this.hashToken}`;
    }

    if (this.authToken) {
      const decodedToken = jwtDecode(this.authToken);
      const expiryTime = new Date(decodedToken.exp * 1000);
      try {
        const adjustedExpiryTime = new Date(
          expiryTime.getTime() - thresholdTime
        );
        const currentDateTime = new Date();

        if (adjustedExpiryTime < currentDateTime) {
          await TokenManager.refreshToken();
          request.headers.Authorization = `Bearer ${getCookie(
            "KC_ACCESS_TOKEN"
          )}`;
        } else {
          request.headers.Authorization = `Bearer ${this.authToken}`;
        }
      } catch (error) {
        console.error("Error during token refresh:", error);
      }
    }

    if (this.userId) {
      request.headers["x-prsn-user-id"] = this.userId;
    }
    if (this.contentType) {
      request.headers["content-type"] = this.contentType;
    }
    if (this.loanStage) {
      request.params = {
        loanStage: this.encode(this.loanStage.toString())
      };
    }
    if (this.executeRule) {
      request.params = {
        loanStage: this.encode(this.loanStage.toString()),
        executeRule: this.executeRule,
        loanId: this.encode(this.loanId.toString())
      };
    }

    // Add custom headers dynamically
    Object.entries(this.customHeaders).forEach(([key, value]) => {
      request.headers[key] = value;
    });

    return request;
  };

  setUserId = (id) => {
    this.userId = id;
  };

  clearUserId = () => {
    this.userId = null;
  };

  setAuthToken = (token) => {
    this.authToken = token;
  };

  setHashToken = (hashToken) => {
    this.hashToken = hashToken;
  };

  clearHashToken = () => {
    this.hashToken = null;
  };

  setLoanStageQueryParams = (loanStage) => {
    this.loanStage = loanStage;
  };

  clearAuthToken = () => {
    this.authToken = null;
  };

  setContentType = (type) => {
    this.contentType = type;
  };

  setExecuteRule = (loanStage, executeRule, loanId) => {
    this.loanId = loanId;
    this.loanStage = loanStage;
    this.executeRule = executeRule;
  };

  setResponseInterceptor = (success, error) => {
    this._httpClient.interceptors.response.use(success, error);
  };

  _makeRequest = (method, url, queryParams, body, options = {}) => {
    const { signal, ...restOptions } = options;
    let request;

    return new Promise((resolve, reject) => {
      switch (method) {
        case "GET":
          request = this._httpClient.get(url, {
            ...restOptions,
            params: queryParams,
            signal
          });
          break;
        case "POST":
          request = this._httpClient.post(url, body, {
            ...restOptions,
            params: queryParams,
            signal
          });
          break;
        case "PUT":
          request = this._httpClient.put(url, body, {
            ...restOptions,
            params: queryParams,
            signal
          });
          break;
        case "PATCH":
          request = this._httpClient.patch(url, body, {
            ...restOptions,
            params: queryParams,
            signal
          });
          break;
        case "DELETE":
          request = this._httpClient.delete(
            url,
            {
              ...restOptions,
              data: body,
              params: queryParams,
              signal
            });
          break;

        default:
          reject(new Error("Method not supported"));
          return;
      }

      request.then(
        (result) => {
          resolve(result);
        },
        (error) => {
          console.log("Network Error", error);
          reject(error);
        }
      );
    });
  };

  get = (url, queryParams, options = {}, body = {}) => {
    return this._makeRequest("GET", url, queryParams, body, options);
  };

  post = (url, body, queryParams, options = {}) => {
    return this._makeRequest("POST", url, queryParams, body, options);
  };

  put = (url, body, queryParams, options = {}) => {
    return this._makeRequest("PUT", url, queryParams, body, options);
  };

  patch = (url, body, queryParams, options = {}) => {
    return this._makeRequest("PATCH", url, queryParams, body, options);
  };

  delete = (url, body, queryParams, options = {}) => {
    return this._makeRequest("DELETE", url, queryParams, body, options);
  };
}
